import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import * as amplitude from "@amplitude/analytics-browser";
import { useTranslation } from "react-i18next";

import ProgressHeader from "./ProgressHeader";
import { getBridgeBlocks, getBridgeHeaderStyle } from "./bridgeCms";
import { BLOCKS_CSS, QUESTION_DEFS, renderBlocksHTML } from "./bridgeBlocks";

type BridgeScreenProps = {
  onContinue: () => void;
  onAnswer?: (questionName: string, value: string) => void;
};

const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
`;

// Centered, mobile-first column that hosts the block-rendered HTML. The blocks
// themselves carry their own max-width/padding (see BLOCKS_CSS .bb), so this
// wrapper just centers them and lets the column grow to fill the AppContainer.
const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  width: 100%;
  max-width: calc(80 * var(--base-unit, 8px));
  margin: 0 auto;
  box-sizing: border-box;
`;

// Inject the canonical blocks CSS exactly once per mount.
let cssInjected = false;
function ensureBlocksCss() {
  if (cssInjected || typeof document === "undefined") return;
  if (document.getElementById("bridge-blocks-css")) {
    cssInjected = true;
    return;
  }
  const style = document.createElement("style");
  style.id = "bridge-blocks-css";
  style.textContent = BLOCKS_CSS;
  document.head.appendChild(style);
  cssInjected = true;
}

const BridgeScreen: React.FC<BridgeScreenProps> = ({ onContinue, onAnswer }) => {
  const location = useLocation();
  // Re-render the block HTML when the language changes (quiz labels inside it are localized).
  const { i18n } = useTranslation();
  const language = i18n.language;
  const utmAngle = new URLSearchParams(location.search).get("utm_angle") || "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const blocks = useMemo(() => getBridgeBlocks(utmAngle), [utmAngle, language]);
  const hasBlocks = blocks.length > 0;
  const headerStyle = useMemo(() => getBridgeHeaderStyle(utmAngle), [utmAngle]);
  // A bridge with a Question block REQUIRES an answer before continuing: its survey
  // page may be omitted from the Model (merged), so skipping via a CTA would drop a
  // required answer with no way to re-ask it. The question becomes the gate.
  const hasQuestion = useMemo(() => blocks.some((b) => b.type === "question"), [blocks]);

  const html = useMemo(
    () => (hasBlocks ? renderBlocksHTML(blocks, { questionDefs: QUESTION_DEFS }) : ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks, hasBlocks, language]
  );

  // Guard so a CTA / choice click only continues once.
  const continuedRef = useRef(false);
  // Whether the merged question (if any) has been answered on the bridge.
  const answeredRef = useRef(false);

  ensureBlocksCss();

  useEffect(() => {
    if (!hasBlocks) return;
    amplitude.track("bridge_screen_viewed", {
      utm_angle: utmAngle,
      experiment_group: "treatment",
    });
  }, [hasBlocks, utmAngle]);

  useEffect(() => {
    if (!hasBlocks) {
      onContinue();
    }
  }, [hasBlocks, onContinue]);

  if (!hasBlocks) {
    return null;
  }

  const handleContinue = () => {
    if (continuedRef.current) return;
    continuedRef.current = true;
    amplitude.track("bridge_screen_cta_clicked", {
      utm_angle: utmAngle,
    });
    onContinue();
  };

  // Event delegation: a single click handler drives both the CTA and the inline
  // Merged Question. We render markup (not React nodes) so we read intent off the
  // closest [data-cta] / [data-choice] element.
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target || !target.closest) return;

    const choiceEl = target.closest("[data-choice]") as HTMLElement | null;
    if (choiceEl) {
      const question = choiceEl.getAttribute("data-question") || "";
      const value = choiceEl.getAttribute("data-value") || "";
      // Snappy visual feedback: mark this option selected before continuing.
      const group = choiceEl.parentElement;
      if (group) {
        group.querySelectorAll(".is-selected").forEach((el) => el.classList.remove("is-selected"));
      }
      choiceEl.classList.add("is-selected");
      answeredRef.current = true;
      if (question && value) onAnswer?.(question, value);
      handleContinue();
      return;
    }

    const ctaEl = target.closest("[data-cta]") as HTMLElement | null;
    if (ctaEl) {
      // A CTA must not skip an unanswered merged question — nudge to it instead.
      if (hasQuestion && !answeredRef.current) {
        const q = e.currentTarget.querySelector(".bb-q") as HTMLElement | null;
        if (q) {
          q.classList.add("bb-q--nudge");
          q.scrollIntoView({ behavior: "smooth", block: "center" });
          window.setTimeout(() => q.classList.remove("bb-q--nudge"), 900);
        }
        return;
      }
      ctaEl.classList.add("is-selected");
      handleContinue();
    }
  };

  return (
    <>
      <HeaderContainer>
        {headerStyle === "logo" ? (
          <div className="bb-logoheader">
            <img className="bb-logoheader__img" src="/logo.svg" alt="Built With Science" />
          </div>
        ) : (
          <ProgressHeader progress={0} title="Getting Started" />
        )}
      </HeaderContainer>
      <Content
        onClick={handleClick}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
};

export default BridgeScreen;
